// ABOUTME: Hook for fetching public site-wide settings like email verification requirements
// ABOUTME: Uses simple in-memory cache to avoid repeated API calls

import { useState, useEffect } from 'react';
import api from '../services/api';

interface SiteSettings {
  requireEmailVerification: boolean;
  defaultThemeName: string;
  crossingEnabled: boolean;
}

// Simple in-memory cache
let cachedSettings: SiteSettings | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 60 * 1000; // 1 minute

export function invalidateSiteSettingsCache() {
  cachedSettings = null;
  cacheTimestamp = 0;
}

export function useSiteSettings() {
  const [data, setData] = useState<SiteSettings | undefined>(cachedSettings || undefined);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(!cachedSettings);

  useEffect(() => {
    const fetchSettings = async () => {
      // Return cached data if fresh
      if (cachedSettings && Date.now() - cacheTimestamp < CACHE_DURATION) {
        setData(cachedSettings);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await api.get<SiteSettings>('/api/sitesettings');
        cachedSettings = response.data;
        cacheTimestamp = Date.now();
        setData(response.data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch site settings'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  return { data, error, isLoading };
}
